import { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  styled,
  Hidden,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { motion } from "framer-motion";
import { useReactQuery } from "../../hooks/useReactQuery";
import { filterActiveListings, filterListingsWithPhotos } from "../../actions";
import { listingURL } from "../../api/apis";

import LoadingPropertyItem from "../LoadingPropertyItem";
import ListingCard from "../ListingCard";
import EmptyListView from "../EmptyListView";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const SyletedNavBtn = styled(Button)(({ theme }) => ({
  width: "2.5rem",
  minWidth: "2.5rem",
  height: "2.5rem",
  padding: 0,
  margin: 0,
  borderRadius: "2.5rem",
  backgroundColor: "white",
  border: "1px solid",
  borderColor: theme.palette.alternate.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
}));

export default function PropertyListing({
  listKey = null,
  title = "New Selling Homes",
  subTitle = "",
  listings = null,
  isLoading = false,
  browseMoreText = "Browse more properties",
  browseMoreAction,
  autoSlides = false,
  styles = {},
  contentStyles = {
    px: 2.5,
  },
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const swiperRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: res } = await listingURL.get(
        `residential/merge_listings/?limit=32`
      );
      const results = filterListingsWithPhotos(res?.data);
      const activeListings = filterActiveListings(results);
      const randomSample = _.sampleSize(activeListings, 32);
      return randomSample;
    } catch (error) {
      console.error(error.response);
      return error;
    }
  }, []);

  const { isFetching: load, data: defaultList } = useReactQuery(
    ["property-listings", listKey],
    fetchData,
    {
      enabled: listings === null && isLoading === false,
    }
  );
  const data = defaultList ?? listings;

  const hasMoreSlides = useMemo(() => {
    return data?.length > 0 && sm ? data?.length > 3 : data?.length > 1;
  }, [sm, data]);

  const handleNavigation = (type) => {
    if (swiperRef.current) {
      switch (type) {
        case "prev":
          swiperRef.current.slidePrev();
          break;
        case "next":
          swiperRef.current.slideNext();
          break;
        default:
          break;
      }
    }
  };

  // Browse more component
  const BrowseMore = () => (
    <Button
      variant="contained"
      color="primary"
      size="small"
      disableElevation
      sx={{
        borderRadius: "50px",
        textTransform: "capitalize",
        padding: "12px 20px",
        height: "3rem",
        width: { xs: "100%", md: "auto" },
      }}
      onClick={() => browseMoreAction ?? navigate("/homes-for-sale")}
    >
      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <Typography fontSize={"1rem"}>{browseMoreText}</Typography>
        <ArrowForwardRoundedIcon
          sx={{
            color: "text.orange.1",
          }}
        />
      </Stack>
    </Button>
  );

  return (
    <Box
      sx={{
        py: 5,
        backgroundColor: "paper.main",
        ...styles,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            ...contentStyles,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: { xs: 2, md: 3 },
            }}
          >
            <Stack gap={1}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight="600"
                fontSize={{ xs: "1.75rem", md: "2rem" }}
              >
                {title}
              </Typography>
              {subTitle && (
                <Typography
                  variant="body1"
                  fontSize={{ xs: "1rem:", sm: "18px" }}
                  color={"neutral.dark"}
                >
                  {subTitle}
                </Typography>
              )}
            </Stack>

            <Hidden mdDown>
              {browseMoreText && data?.length > 0 && <BrowseMore />}
            </Hidden>
          </Box>

          <Box>
            <Swiper
              modules={[Autoplay, Navigation]}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              loop
              slidesPerView={autoSlides ? "auto" : undefined}
              spaceBetween={autoSlides ? 16 : undefined}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={
                autoSlides
                  ? undefined
                  : {
                      0: {
                        slidesPerView: 1.1,
                        spaceBetween: 10,
                        centeredSlides: true,
                        centeredSlidesBounds: true,
                      },
                      600: {
                        slidesPerView: 2,
                        spaceBetween: 10,
                      },
                      900: {
                        slidesPerView: 3,
                        spaceBetween: 16,
                      },
                      1050: {
                        slidesPerView: 3.5,
                        spaceBetween: 16,
                      },
                      1200: {
                        slidesPerView: 4,
                        spaceBetween: 16,
                      },
                    }
              }
              style={{ padding: "8px 1px 1.5rem 0" }}
            >
              {load || isLoading ? (
                Array(4)
                  .fill()
                  .map((_, ld) => {
                    return (
                      <SwiperSlide key={ld}>
                        <LoadingPropertyItem />
                      </SwiperSlide>
                    );
                  })
              ) : data?.length > 0 ? (
                <motion.div
                  className="slide-more"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {data?.map((item) => (
                    <SwiperSlide
                      key={item.id}
                      style={{
                        width: autoSlides
                          ? sm
                            ? "360px"
                            : "315px"
                          : "inherit",
                      }}
                    >
                      <motion.div
                        key={item.id}
                        whileHover={{ y: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        style={{ width: "100%" }}
                      >
                        <ListingCard
                          {...item}
                          id={item.id}
                          listDate={item.listDate}
                          mlsId={item.mlsId}
                          status={item.status}
                          address={_.startCase(
                            item.address?.full +
                              `${
                                item?.address?.city && `, ${item.address?.city}`
                              }` +
                              `${
                                item?.address?.state &&
                                `, ${item.address?.state}`
                              }` +
                              `${
                                item?.address?.postalCode &&
                                `, ${item.address?.postalCode}`
                              }`
                          )}
                          price={item?.listPrice?.toLocaleString()}
                          image={
                            item?.photos[1] ? item?.photos[1] : item?.photos[0]
                          }
                          photos={item.photos}
                          bed={item.property?.bedrooms}
                          bath={item.property?.bathsFull}
                          area={item.property?.area}
                          vendor={item?.listing === "mls" ? "mls" : "crmls"}
                          active={item?.mls?.status}
                          virtualTourUrl={item?.virtualTourUrl}
                          office={item?.office}
                          agentListed={item?.agentListed ?? false}
                        />
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </motion.div>
              ) : (
                <EmptyListView message="No listings available" suggestion="" />
              )}
            </Swiper>

            <Box sx={{ display: hasMoreSlides ? "block" : "none" }}>
              <Hidden mdDown>
                {!(load || isLoading) && (
                  <Stack
                    direction={"row"}
                    justifyContent={"flex-end"}
                    alignItems={"center"}
                    gap={2.5}
                  >
                    <SyletedNavBtn
                      color="alternate"
                      onClick={() => handleNavigation("prev")}
                    >
                      <ArrowBackOutlinedIcon
                        fontSize="small"
                        color="alternate"
                      />
                    </SyletedNavBtn>
                    <SyletedNavBtn
                      color="alternate"
                      onClick={() => handleNavigation("next")}
                    >
                      <ArrowForwardRoundedIcon
                        fontSize="small"
                        color="alternate"
                      />
                    </SyletedNavBtn>
                  </Stack>
                )}
              </Hidden>
              <Hidden mdUp>
                {browseMoreText && data?.length > 0 && <BrowseMore />}
              </Hidden>
            </Box>
          </Box>
        </Container>
      </motion.div>
    </Box>
  );
}
